import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { adminResetMfa } from '../../api/auth.js'

/**
 * Platform-admin recovery tool: unlock — or fully reset — another user's MFA.
 * Rendered only for is_staff / is_superuser accounts (gated by the caller).
 */
export default function AdminMfaResetCard() {
  const [username, setUsername] = useState('')
  const [result, setResult] = useState(null)

  const resetMut = useMutation({
    mutationFn: ({ disable }) => adminResetMfa({ username: username.trim(), disable }),
    onSuccess: (data) => setResult(data),
  })

  function doUnlock() {
    setResult(null)
    if (!username.trim()) return
    resetMut.mutate({ disable: false })
  }

  function doFullReset() {
    setResult(null)
    const name = username.trim()
    if (!name) return
    if (window.confirm(
      `Fully reset MFA for "${name}"?\n\n` +
      `This turns MFA OFF and DELETES their recovery codes. Only do this after ` +
      `verifying their identity out-of-band — they will sign in with their ` +
      `password alone until they re-enable MFA.`
    )) {
      resetMut.mutate({ disable: true })
    }
  }

  return (
    <Card>
      <CardHeader
        title="Admin · reset a user's MFA"
        subtitle="For a user who is locked out or has lost access to both their email and recovery codes. Verify their identity first."
      />

      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <label className="text-xs text-slate-600 flex flex-col gap-1 flex-1">
          <span>Username</span>
          <input
            value={username}
            onChange={(e) => { setUsername(e.target.value); setResult(null) }}
            placeholder="e.g. seed_doctor"
            className="border rounded px-2 py-1.5 text-sm"
          />
        </label>
        <button
          onClick={doUnlock}
          disabled={resetMut.isPending || !username.trim()}
          className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
        >
          {resetMut.isPending ? 'Working…' : 'Unlock'}
        </button>
        <button
          onClick={doFullReset}
          disabled={resetMut.isPending || !username.trim()}
          className="text-sm border border-rose-300 text-rose-700 hover:bg-rose-50 rounded px-3 py-2 disabled:opacity-60"
        >
          Full reset
        </button>
      </div>

      <p className="mt-2 text-[11px] text-slate-500">
        <b>Unlock</b> clears the lockout and keeps MFA on (recovery codes preserved).
        {' '}<b>Full reset</b> additionally turns MFA off and wipes recovery codes so the user re-enrols.
      </p>

      {resetMut.error && <div className="mt-3"><ErrorBanner error={resetMut.error} /></div>}
      {result && (
        <div className="mt-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
          Done — {result.disabled
            ? 'MFA disabled and recovery codes wiped; the user re-enrols on next sign-in.'
            : 'account unlocked; MFA is still on.'}
        </div>
      )}
    </Card>
  )
}
